import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { Dispatch, SetStateAction, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import deleteRedIcon from '../../../../../../assets/delete-red.svg';
import { CompleteOperationType, StartOperationType } from '../../../../../../model/alerts.model';
import { modalReducer } from '../../../../../../reducers/modalReducer';
import { useAppDispatch } from '../../../../../../store/hooks';
import { deleteCalendarEvent } from '../../../../../../store/slices/calendarSlice';
import zeplinColor from '../../../../../../theme/Color';
import { CalendarEvent } from '../../../../../../utilities/eventUtilies';
import CustomButton from '../../../../../shared/CustomButton/CustomButton';
import CustomDeleteModal from '../../../../../shared/CustomDeleteModal/CustomDeleteModal';
import CustomPopOver from '../../../../../shared/CustomPopOver/CustomPopOver';
import { usePermissions } from '../../../../../../customHook/usePermissions';

interface EventPopOverContentProps {
  anchorEl: HTMLDivElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
  id: 'simple-popover' | undefined;
  selectedEvent: CalendarEvent;
  handleCopyLink: (link: string) => void;
  handleShowGuests: () => void;
  showGuests: boolean;
  onEdit: () => void;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}
export const EventPopOverContent = ({
  setAnchorEl,
  anchorEl,
  id,
  selectedEvent,
  handleCopyLink,
  handleShowGuests,
  showGuests,
  onEdit,
  startOperation,
  completeOperation,
}: EventPopOverContentProps) => {
  const client = useLocation();
  const clientId = client.state?.client.AccountId;
  const dispatch = useAppDispatch();
  const isHuddle = selectedEvent.type === 'Huddle';
  const { hasAction } = usePermissions();
  const canEdit = hasAction('Dashboard', 'Calendar', 'edit');
  const canDelete = hasAction('Dashboard', 'Calendar', 'delete');

  const [modalState, modalDispatch] = useReducer(modalReducer, {
    delete: false,
  });

  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    const platformKey = selectedEvent.selectedCalendar || 'huddle';
    const activeClientId = clientId || undefined;

    startOperation('delete', 'Deleting Event...');

    try {
      await dispatch(
        deleteCalendarEvent({
          type: platformKey,
          eventId: selectedEvent.id,
          activeClientId,
        }),
      ).unwrap();
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      setAnchorEl(null);
      completeOperation('delete', 'Event Deleted Successfully');
    } catch (error) {
      completeOperation('delete', 'Failed to delete event', true);
    }
  };

  return (
    <CustomPopOver
      setAnchorEl={setAnchorEl}
      anchorEl={anchorEl}
      vertical='top'
      horizontal='left'
      id={id}
      sx={{
        borderRadius: '14px',
        boxShadow: 'rgba(0, 0, 0, 0.15)',
        marginLeft: '-25rem',
      }}
    >
      <Box
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '380px',
          maxHeight: '30rem',
          wordWrap: 'break-word',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            wordWrap: 'break-word',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              position: 'sticky',
              top: 0,
              backgroundColor: zeplinColor.Neutral1,
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <Typography variant='responsiveHeading1' color={zeplinColor.BasicBlack}>
                {selectedEvent?.meetingName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img
                  src={'/assets/edit-thin.svg'}
                  alt='close'
                  style={{
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    width: '22px',
                    height: '22px',
                    opacity: canEdit ? 1 : 0.4,
                  }}
                  onClick={canEdit ? onEdit : undefined}
                />
                <img
                  src={deleteRedIcon}
                  alt='close'
                  style={{
                    cursor: canDelete ? 'pointer' : 'not-allowed',
                    width: '22px',
                    height: '22px',
                    opacity: canDelete ? 1 : 0.4,
                  }}
                  onClick={
                    canDelete ? () => modalDispatch({ type: 'OPEN', payload: 'delete' }) : undefined
                  }
                />
                <img
                  src={'/assets/close-icon.svg'}
                  alt='close'
                  style={{ cursor: 'pointer', width: '22px', height: '22px' }}
                  onClick={() => setAnchorEl(null)}
                />
              </Box>
            </Box>
            <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
              {dayjs(selectedEvent?.start).format('dddd, MMMM D, YYYY | h:mm a')} -{' '}
              {dayjs(selectedEvent?.end).format('h:mm a')}
            </Typography>
          </Box>
          {selectedEvent?.description ? (
            <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack} sx={{}}>
              {parse(selectedEvent?.description)}
            </Typography>
          ) : (
            ''
          )}
        </Box>
        <Box sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {!isHuddle && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <a
                href={selectedEvent?.meetingLink ?? ''}
                target='_blank'
                rel='noopener noreferrer'
                style={{ textDecoration: 'none', flex: '1' }}
              >
                <CustomButton
                  disabled={false}
                  variant='contained'
                  text={'Join Meeting'}
                  onClick={undefined}
                  icon={
                    <img
                      src={'/assets/arrow-white.svg'}
                      style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                      alt='white arrow'
                    />
                  }
                  iconPosition='end'
                  style={{
                    borderRadius: '14px',
                    padding: '14px 16px',
                    height: '48px',
                  }}
                  buttonVariant='responsiveButtonMedium'
                />
              </a>
              <img
                src={'/assets/content_copy.svg'}
                alt='copy'
                style={{ cursor: 'pointer' }}
                width='24px'
                height='24px'
                onClick={() =>
                  selectedEvent?.meetingLink && handleCopyLink(selectedEvent?.meetingLink)
                }
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              cursor: 'pointer',
              gap: '16px',
            }}
            onClick={handleShowGuests}
          >
            <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'flex-start' }}>
              <img
                src={'/assets/user-multiple-02.svg'}
                alt='guest'
                style={{ cursor: 'pointer' }}
                width='24px'
                height='24px'
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  maxWidth: '200px',
                }}
              >
                <Typography variant='responsiveBody2'>
                  {selectedEvent?.invitees.length} Guests
                </Typography>
                {showGuests &&
                  selectedEvent?.invitees.map((invitee: any, key: number) => {
                    const name = invitee?.name;

                    return (
                      <Box
                        key={invitee.id}
                        sx={{
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            borderRadius: '100px',
                            padding: '10px',
                            width: { xs: '32px', xl: '32px' },
                            height: { xs: '32px', xl: '32px' },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: invitee.profileImage
                              ? 'transparent'
                              : zeplinColor[
                                  key % 8 === 0
                                    ? 'Brand1'
                                    : (`USER_PROFILE_COLOR${key % 8}` as keyof typeof zeplinColor)
                                ],
                            position: 'relative',
                          }}
                        >
                          {invitee?.profileImage ? (
                            <img
                              src={invitee?.profileImage}
                              alt='guest'
                              style={{ cursor: 'pointer', borderRadius: '50%' }}
                              width='32px'
                              height='32px'
                            />
                          ) : (
                            <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                              {name
                                ?.split(' ')
                                .map((value: any, index: number) =>
                                  name && (index === 0 || index === name?.split(' ').length - 1)
                                    ? value[0].toUpperCase()
                                    : '',
                                )}
                            </Typography>
                          )}
                          {invitee?.confirmation !== 'needsAction' &&
                            invitee?.status !== 'pending' &&
                            invitee?.confirmation !== 'none' && (
                              <img
                                src={
                                  invitee?.confirmation === 'accepted'
                                    ? '/assets/event-checked.svg'
                                    : invitee?.confirmation === 'declined'
                                      ? '/assets/event-not-checked.svg'
                                      : ''
                                }
                                alt='confirmation'
                                style={{ position: 'absolute', bottom: 0, right: 0 }}
                                width='14px'
                                height='14px'
                              />
                            )}
                        </Box>
                        <Typography
                          variant='responsiveBody3'
                          color={zeplinColor.BasicBlack}
                          sx={{ wordWrap: 'break-word' }}
                        >
                          {name}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>
            </Box>

            <img
              src={showGuests ? '/assets/up-arrow.svg' : '/assets/down-arrow.svg'}
              alt='guest'
              style={{ cursor: 'pointer' }}
              width='24px'
              height='24px'
            />
          </Box>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <img
              src={'/assets/calendar.svg'}
              alt='guest'
              style={{ cursor: 'pointer' }}
              width='24px'
              height='24px'
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                wordWrap: 'break-word',
              }}
            >
              <Typography variant='responsiveBody2' sx={{ wordBreak: 'break-word' }}>
                {selectedEvent?.organizer?.organizerEmail}
              </Typography>
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                Created by
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleDeleteEvent}
        title='Delete Event'
        description='Deleting this meeting will remove it for you and any invited guests. This action cannot be undone. are you sure you want to delete?'
      />
    </CustomPopOver>
  );
};
